'use client'

import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'

import { WidgetButtons, WidgetMedia, WidgetRichText, readButton } from '../widget-content'
import { getSafeHref } from '../widget-utils'

import styles from './product-comparison-carousel.module.scss'

import type { ProductComparisonCarouselWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { CSSProperties, MouseEvent } from 'react'

/**
 * PRODUCT_COMPARISON_CAROUSEL widget (non-DS / legacy path).
 *
 * A centered serif header with prev/next scroll arrows over a horizontal row of comparison cards.
 * Each card stacks a square product image, an uppercase name, a divider-separated spec list
 * (label over value), and a full-width CTA. Styling lives in `product-comparison-carousel.module.scss`.
 * Data comes from `data.productComparisonCarouselData`.
 */

// Non-PL parity (`scrollToTopWhenOnTarget`, feature-section-7-landing-new): the current product's
// own CTA smooth-scrolls back to the buy box instead of navigating. Non-PL rides NextLink (same-URL
// nav is a soft no-op); these are plain anchors, so suppress the default full reload explicitly.
// Exact-target check rather than non-PL's substring test: with preventDefault in play, a substring
// false positive on a prefix-sibling slug (…/pillowcase vs …/pillowcase-set-of-2) would suppress a
// real navigation. "On target" = same origin + pathname AND every CTA query param already carried
// by the current URL — so a differing query (e.g. `?color=`) still navigates, matching non-PL.
const scrollToTopWhenOnTarget = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
  const target = new URL(href, window.location.href)
  const current = new URL(window.location.href)
  const onTarget =
    target.origin === current.origin &&
    target.pathname === current.pathname &&
    [...target.searchParams].every(([key, value]) => current.searchParams.get(key) === value)
  if (onTarget) {
    event.preventDefault()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

/** The card's navigation target: the first button's allowlisted href (what the CTA itself links to). */
const getCardHref = (buttons: readonly Record<string, unknown>[]): string | undefined => {
  for (const raw of buttons) {
    const parsed = readButton(raw)
    const href = parsed && getSafeHref(parsed.url)
    if (href) return href
  }
  return undefined
}

/** Decorative chevron-arrow glyph for the scroll controls (label lives on the button's aria-label). */
function ArrowIcon({ flip = false }: Readonly<{ flip?: boolean }>) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={flip ? { transform: 'scaleX(-1)' } : undefined}
    >
      <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function ProductComparisonCarouselLegacy(props: PLTypedWidgetProps<ProductComparisonCarouselWidgetData>) {
  const data = props.data.productComparisonCarouselData
  const items = data?.items ?? []
  // Comparison items share the same spec keys, so the row count is uniform. Drives the shared grid
  // row tracks (below) so every card's spec rows line up even when a value wraps to extra lines.
  const specRowCount = items[0]?.specs.length ?? 0
  const rowRef = useRef<HTMLDivElement>(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  // Enable each arrow only when there's room to scroll that way (target greys out the prev arrow
  // at the start). Re-measured on mount, on scroll, and when the item count changes.
  const updateScrollState = useCallback(() => {
    const row = rowRef.current
    if (!row) return
    setCanScrollPrev(row.scrollLeft > 1)
    setCanScrollNext(Math.ceil(row.scrollLeft + row.clientWidth) < row.scrollWidth)
  }, [])

  useEffect(() => {
    updateScrollState()
  }, [updateScrollState, items.length])

  if (items.length === 0) return null

  const scrollByCard = (direction: 1 | -1) => {
    const row = rowRef.current
    if (!row) return
    row.scrollBy({ left: direction * row.clientWidth * 0.8, behavior: 'smooth' })
  }

  return (
    <section className={styles.section} data-testid="product-comparison-carousel-widget">
      <div className={styles.header}>
        <WidgetRichText data={data?.heading} className={styles.heading} />
        <WidgetRichText data={data?.subHeading} className={styles.subheading} />
        {/* eslint-disable i18next/no-literal-string -- carousel nav aria-labels; no next/previous locale keys exist yet, localization tracked as follow-up */}
        <div className={styles.arrows}>
          <button
            type="button"
            className={styles.arrow}
            aria-label="Previous"
            disabled={!canScrollPrev}
            onClick={() => {
              scrollByCard(-1)
            }}
          >
            <ArrowIcon flip />
          </button>
          <button
            type="button"
            className={styles.arrow}
            aria-label="Next"
            disabled={!canScrollNext}
            onClick={() => {
              scrollByCard(1)
            }}
          >
            <ArrowIcon />
          </button>
        </div>
        {/* eslint-enable i18next/no-literal-string */}
      </div>

      {/* Flattened grid: every card's cells share the same row tracks (image · name · one track per
          spec · CTA) via `grid-auto-flow: column`, so spec rows align across cards regardless of how
          a value wraps. `--spec-row-count` sizes the shared spec tracks. */}
      <div
        ref={rowRef}
        className={styles.row}
        onScroll={updateScrollState}
        style={{ '--spec-row-count': specRowCount } as CSSProperties}
      >
        {items.map((item, index) => {
          const cardHref = getCardHref(item.buttons)
          const cells = (
            <>
              <div className={styles.cellMedia}>
                <WidgetMedia media={item.media} className={styles.media} />
              </div>
              <div className={styles.cellName}>
                <WidgetRichText data={item.name} className={styles.name} />
              </div>
              {item.specs.map((spec, specIndex) => (
                <div key={specIndex} className={styles.specRow}>
                  <WidgetRichText data={spec.label} className={styles.specLabel} />
                  <WidgetRichText data={spec.value} className={styles.specValue} />
                </div>
              ))}
            </>
          )
          return (
            // `name` is rich text (no plain title), so key off its Contentful id when present;
            // index fallback covers items the CMS didn't stamp. These lists aren't reordered.
            <Fragment key={(item.name as { contentful_id?: string } | undefined)?.contentful_id ?? index}>
              {/* Non-PL parity (ProductColumn `linkForColumn`): the whole card — image, name, specs —
                  links to the CTA target; the CTA stays a separate link (anchors can't nest).
                  `display: contents` keeps the cells direct grid children so rows stay aligned. */}
              {cardHref ? (
                <a
                  className={styles.cardLink}
                  href={cardHref}
                  onClick={event => {
                    scrollToTopWhenOnTarget(event, cardHref)
                  }}
                >
                  {cells}
                </a>
              ) : (
                cells
              )}
              <div className={styles.cellCta}>
                <WidgetButtons
                  buttons={item.buttons}
                  linkClassName={styles.ctaLink}
                  onLinkClick={scrollToTopWhenOnTarget}
                />
              </div>
            </Fragment>
          )
        })}
      </div>
    </section>
  )
}
